import React, { useState, useEffect } from 'react';
import { 
  getInfluencerCommissions, 
  getCommissionStats, 
  processBulkPayout,
  getCommissionById 
} from '../../Services/Operations/ProductAPI';
import '../../Styles/ManagementPages.css';
import { useSelector } from 'react-redux';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-checkbox"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th><div className="skeleton-checkbox-header"></div></th>
          <th>Influencer</th>
          <th>Booking Details</th>
          <th>Commission</th>
          <th>Date</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonFilterPanel = () => (
  <div className="filter-panel skeleton">
    <div className="filter-header">
      <div className="skeleton-title"></div>
      <div className="skeleton-close"></div>
    </div>
    <div className="filter-grid">
      {[1, 2, 3].map((i) => (
        <div key={i} className="filter-group">
          <div className="skeleton-label"></div>
          <div className="skeleton-input"></div>
        </div>
      ))}
    </div>
    <div className="filter-actions">
      <div className="skeleton-button"></div>
      <div className="skeleton-button"></div>
    </div>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4].map((i) => (
          <div key={i}>
            <div className="skeleton-subtitle"></div>
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="skeleton-detail-row">
                <div className="skeleton-label"></div>
                <div className="skeleton-value"></div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const SkeletonPaymentModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="skeleton-detail-row">
            <div className="skeleton-label"></div>
            <div className="skeleton-value"></div>
          </div>
        ))}
        <div className="skeleton-input"></div>
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const InfluencerCommission = () => {
  const [commissions, setCommissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searching, setSearching] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [dateFilter, setDateFilter] = useState('All Time');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    influencerId: '',
    startDate: '',
    endDate: ''
  });
  const [isFiltered, setIsFiltered] = useState(false);
  
  const [viewingCommission, setViewingCommission] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [bulkPaymentDate, setBulkPaymentDate] = useState('');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCommissions, setTotalCommissions] = useState(0);
  const [pageSize] = useState(10);

  const { token } = useSelector(state => state.user);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCommissions();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm, statusFilter, dateFilter, filters]);

  useEffect(() => {
    fetchStats();
  }, [commissions]);

  const fetchCommissions = async () => {
    try {
      setSearching(true);
      setLoading(true);
      setError(null);
      
      const filterParams = {};
      
      if (searchTerm) {
        filterParams.search = searchTerm;
      }
      
      if (statusFilter !== 'All Status') {
        filterParams.status = statusFilter.toLowerCase();
      }
      
      let usePresetDates = dateFilter !== 'All Time';
      
      if (usePresetDates) {
        let startDate = new Date();
        let endDate = new Date();
        
        switch (dateFilter) {
          case 'Today':
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          case 'This Week':
            startDate.setDate(startDate.getDate() - startDate.getDay());
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          case 'This Month':
            startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
            endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          default:
            break;
        }
        
        filterParams.startDate = startDate.toISOString().split('T')[0];
        filterParams.endDate = endDate.toISOString().split('T')[0];
      } else {
        if (filters.startDate) filterParams.startDate = filters.startDate;
        if (filters.endDate) filterParams.endDate = filters.endDate;
      }
      
      if (filters.influencerId) {
        filterParams.influencerId = filters.influencerId;
      }

      const params = {
        page: currentPage,
        limit: pageSize,
        ...filterParams
      };

      console.log('Fetching commissions with params:', params);
      const response = await getInfluencerCommissions(token, params);
      
      if (response && response.success) {
        setCommissions(response.commissions || []);
        
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages || 1);
          setTotalCommissions(response.pagination.total || 0);
        } else {
          setTotalPages(1);
          setTotalCommissions(response.commissions?.length || 0);
        }
        
        const hasActiveFilters = 
          searchTerm !== '' || 
          statusFilter !== 'All Status' || 
          dateFilter !== 'All Time' ||
          filters.startDate ||
          filters.endDate ||
          filters.influencerId;
        setIsFiltered(hasActiveFilters);
      } else {
        setError(response?.message || 'Failed to fetch commissions');
        setCommissions([]);
        setTotalPages(1);
        setTotalCommissions(0);
      }
    } catch (err) {
      console.error('Error fetching commissions:', err);
      setError('Failed to load commissions. Please try again.');
      setCommissions([]);
      setTotalPages(1);
      setTotalCommissions(0);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const fetchStats = async () => {
    try {
      const filterParams = {};
      
      if (statusFilter !== 'All Status') {
        filterParams.status = statusFilter.toLowerCase();
      }
      
      if (dateFilter !== 'All Time') {
        let startDate = new Date();
        let endDate = new Date();
        
        switch (dateFilter) {
          case 'Today':
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          case 'This Week':
            startDate.setDate(startDate.getDate() - startDate.getDay());
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          case 'This Month':
            startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
            endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          default:
            break;
        }
        
        filterParams.startDate = startDate.toISOString().split('T')[0];
        filterParams.endDate = endDate.toISOString().split('T')[0];
      } else {
        if (filters.startDate) filterParams.startDate = filters.startDate;
        if (filters.endDate) filterParams.endDate = filters.endDate;
      }
      
      if (filters.influencerId) {
        filterParams.influencerId = filters.influencerId;
      }

      if (searchTerm) {
        filterParams.search = searchTerm;
      }

      const response = await getCommissionStats(token, filterParams);
      if (response && response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCommissionDetails = async (id) => {
    try {
      const response = await getCommissionById(id, token);
      return response;
    } catch (error) {
      console.error('Error fetching commission details:', error);
      return null;
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (e) => {
    setDateFilter(e.target.value);
    setCurrentPage(1);
    if (e.target.value !== 'All Time') {
      setFilters(prev => ({...prev, startDate: '', endDate: ''}));
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('All Status');
    setDateFilter('All Time');
    setFilters({
      influencerId: '',
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
    setIsFiltered(false);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleExportReport = () => {
    if (commissions.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Influencer', 'Email', 'Booking PNR', 'Booking Amount', 'Commission Rate', 'Commission Amount', 'Status', 'Created Date', 'Payment Date'];
    const csvData = commissions.map(commission => [
      commission.influencer?.name || 'N/A',
      commission.influencer?.email || 'N/A',
      commission.booking?.pnr || 'N/A',
      `₹${commission.bookingAmount || 0}`,
      `${commission.commissionRate || 0}%`,
      `₹${commission.amount || 0}`,
      commission.status || 'pending',
      formatDate(commission.createdAt),
      commission.paymentDate ? formatDate(commission.paymentDate) : 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `influencer-commission-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    alert('Influencer commission report exported successfully!');
  };

  const handleViewCommission = async (commission) => {
    try {
      setIsFetchingDetails(true);
      const details = await fetchCommissionDetails(commission._id);
      setViewingCommission(details || commission);
    } catch (error) {
      console.error('Error fetching commission details:', error);
      setViewingCommission(commission);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleProcessPayment = (commission) => {
    setProcessingPayment({
      ...commission,
      type: 'single'
    });
  };

  const handleConfirmPayment = async () => {
    try {
      setError(null);
      const commissionIds = processingPayment.type === 'single' 
        ? [processingPayment._id] 
        : selectedCommissions;

      const response = await processBulkPayout({
        commissionIds,
        paymentDate: bulkPaymentDate || new Date().toISOString().split('T')[0]
      }, token);

      if (response && response.success) {
        await fetchCommissions();
        await fetchStats();
        
        if (processingPayment.type === 'bulk') {
          setSelectedCommissions([]);
        }
        
        setProcessingPayment(null);
        setBulkPaymentDate('');
        alert(`Payment processed successfully!`);
      } else {
        throw new Error(response?.message || 'Failed to process payment');
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      setError('Failed to process payment');
      alert('Failed to process payment. Please try again.');
    }
  };

  const handleBulkPayment = () => {
    const pendingCommissions = commissions.filter(c => c.status === 'pending');
    const selectedPending = pendingCommissions.filter(c => selectedCommissions.includes(c._id));
    
    if (selectedPending.length === 0) {
      alert('Please select pending commissions to pay!');
      return;
    }

    setProcessingPayment({
      type: 'bulk',
      commissions: selectedPending,
      totalAmount: selectedPending.reduce((sum, c) => sum + c.amount, 0)
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const pendingCommissionIds = commissions
        .filter(c => c.status === 'pending')
        .map(c => c._id);
      setSelectedCommissions(pendingCommissionIds);
    } else {
      setSelectedCommissions([]);
    }
  };

  const handleCommissionSelect = (commissionId) => {
    setSelectedCommissions(prev => 
      prev.includes(commissionId) 
        ? prev.filter(id => id !== commissionId)
        : [...prev, commissionId]
    );
  };

  const closeViewModal = () => setViewingCommission(null);
  const closePaymentModal = () => {
    setProcessingPayment(null);
    setBulkPaymentDate('');
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      setSelectedCommissions([]);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      setSelectedCommissions([]);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedCommissions([]);
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setSelectedCommissions([]);
    clearFilters();
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      if (end - start + 1 < maxVisiblePages) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const totalCommission = stats?.totalStats?.totalCommission || 0;
  const totalBookings = stats?.totalStats?.totalBookings || 0;
  
  const paidStats = stats?.summary?.find(s => s._id === 'paid') || { totalAmount: 0, count: 0 };
  const pendingStats = stats?.summary?.find(s => s._id === 'pending') || { totalAmount: 0, count: 0 };
  
  const paidCommission = paidStats.totalAmount || 0;
  const pendingCommission = pendingStats.totalAmount || 0;
  const paidPercentage = totalCommission > 0 ? Math.round((paidCommission / totalCommission) * 100) : 0;

  const getSafeString = (value) => value === null || value === undefined ? 'N/A' : String(value);
  const getSafeNumber = (value) => value === null || value === undefined ? 0 : Number(value);
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch (error) {
      return 'N/A';
    }
  };

  if (loading && commissions.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-select"></div>
            <div className="skeleton-select"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Influencer Commission</h1>
        <div className="header-actions">
          <div style={{ position: 'relative' }}>
            <input 
              type="text" 
              placeholder="Search by influencer, email or coupon code..." 
              className="search-input" 
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: "300px" }}
            />
            {searching && (
              <span style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
              }}>
                searching...
              </span>
            )}
          </div>
          <button className="filter-btn" onClick={toggleFilters}>
            🔍 Filters
          </button>
          <select 
            className="filter-select" 
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <option>All Status</option>
            <option>Paid</option>
            <option>Pending</option>
          </select>
          <select 
            className="filter-select" 
            value={dateFilter}
            onChange={handleDateFilterChange}
          >
            <option>All Time</option>
            <option>Today</option>
            <option>This Week</option>
            <option>This Month</option>
          </select>
         
          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportReport}>
            Export Report
          </button>
          <button 
            className="add-btn" 
            onClick={handleBulkPayment}
            style={{backgroundColor: '#48bb78'}}
            disabled={selectedCommissions.length === 0}
          >
            Pay Selected ({selectedCommissions.length})
          </button>
        </div>
      </div>


      {showFilters && !loading && (
        <div className="filter-panel">
          <div className="filter-header">
            <h3>Filter Commission Data</h3>
            <button className="close-filters" onClick={() => setShowFilters(false)}>
              ×
            </button>
          </div>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Influencer ID</label>
              <input
                type="text"
                name="influencerId"
                value={filters.influencerId}
                onChange={handleFilterChange}
                placeholder="Enter influencer ID"
              />
            </div>
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="apply-filters-btn" onClick={applyFilters}>
              Apply Filters
            </button>
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All
            </button>
          </div>
          {isFiltered && (
            <div className="active-filters">
              <span>Active Filters:</span>
              {searchTerm && (
                <span className="filter-tag">Search: {searchTerm}</span>
              )}
              {statusFilter !== 'All Status' && (
                <span className="filter-tag">Status: {statusFilter}</span>
              )}
              {dateFilter !== 'All Time' && (
                <span className="filter-tag">Date: {dateFilter}</span>
              )}
              {filters.influencerId && (
                <span className="filter-tag">Influencer ID: {filters.influencerId}</span>
              )}
              {filters.startDate && !dateFilter && (
                <span className="filter-tag">From: {filters.startDate}</span>
              )}
              {filters.endDate && !dateFilter && (
                <span className="filter-tag">To: {filters.endDate}</span>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total Commission</h3>
          <p>₹{totalCommission.toLocaleString()}</p>
          <span>All time commissions</span>
        </div>
        <div className="stat-card">
          <h3>Paid</h3>
          <p>₹{paidCommission.toLocaleString()}</p>
          <span>{paidStats.count} payments · {paidPercentage}%</span>
        </div>
        <div className="stat-card">
          <h3>Pending</h3>
          <p>₹{pendingCommission.toLocaleString()}</p>
          <span>{pendingStats.count} pending</span>
        </div>
        <div className="stat-card">
          <h3>Search Results</h3>
          <p>{commissions.length}</p>
          <span>commissions found</span>
        </div>
      </div>


      {stats?.summary && (
        <div className="overall-stats" style={{
          backgroundColor: '#f8f9fa',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #e9ecef'
        }}>
          <h4>Commission Summary by Status</h4>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            {stats.summary.map(stat => (
              <div key={stat._id}>
                <strong style={{textTransform: 'capitalize'}}>{stat._id}:</strong>{' '}
                ₹{stat.totalAmount?.toLocaleString() || 0} ({stat.count || 0} commissions)
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>
                <input 
                  type="checkbox" 
                  onChange={handleSelectAll}
                  checked={commissions.length > 0 && 
                    selectedCommissions.length === commissions.filter(c => c.status === 'pending').length}
                />
              </th>
              <th>Influencer</th>
              <th>Booking Details</th>
              <th>Commission</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {commissions.length > 0 ? (
              commissions.map(commission => (
                <tr key={commission._id}>
                  <td>
                    {commission.status === 'pending' && (
                      <input 
                        type="checkbox" 
                        checked={selectedCommissions.includes(commission._id)}
                        onChange={() => handleCommissionSelect(commission._id)}
                      />
                    )}
                  </td>
                  <td>
                    <div>
                      <div className="name">{getSafeString(commission.influencer?.name)}</div>
                      <div className="email">{getSafeString(commission.influencer?.email)}</div>
                      <div className="referral-code">Ref: {getSafeString(commission.couponCode)}</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="pnr">PNR: {getSafeString(commission.booking?.pnr)}</div>
                      <div className="journey-date">
                        {formatDate(commission.booking?.journeyDetails?.date)}
                      </div>
                      <div className="booking-amount">
                        Booking: ₹{getSafeNumber(commission.bookingAmount).toLocaleString()}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="amount">₹{getSafeNumber(commission.amount).toLocaleString()}</div>
                      <div className="rate">{getSafeNumber(commission.commissionRate)}%</div>
                    </div>
                  </td>
                  <td>
                    {formatDate(commission.createdAt)}
                  </td>
                  <td>
                    <span className={`status-badge ${commission.status}`}>
                      {getSafeString(commission.status).charAt(0).toUpperCase() + getSafeString(commission.status).slice(1)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn view" 
                        onClick={() => handleViewCommission(commission)}
                      >
                        Details
                      </button>
                      {commission.status === 'pending' && (
                        <button 
                          className="action-btn pay" 
                          onClick={() => handleProcessPayment(commission)}
                        >
                          Pay Now
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-data">
                  <p>No commissions found {isFiltered ? 'matching your filters' : ''}.</p>
                  {isFiltered && (
                    <button className="clear-filters-link" onClick={clearFilters}>
                      Clear filters to see all commissions
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      <div className="pagination-container">
        <div className="pagination-info">
          Showing {commissions.length} of {totalCommissions} commissions
          (Page {currentPage} of {totalPages})
          {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
          {isFiltered && !searchTerm && <span className="filtered-indicator"> • Filtered Results</span>}
        </div>
        <div className="pagination-controls">
          <button 
            className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`} 
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          <div className="page-numbers">
            {generatePageNumbers().map(pageNumber => (
              <button
                key={pageNumber}
                className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                onClick={() => handlePageClick(pageNumber)}
                disabled={pageNumber === currentPage}
              >
                {pageNumber}
              </button>
            ))}
            
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="page-dots">...</span>
                <button
                  className={`page-number ${totalPages === currentPage ? 'active' : ''}`}
                  onClick={() => handlePageClick(totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>
          
          <button 
            className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>


      {viewingCommission && !isFetchingDetails && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Influencer Commission Details - {getSafeString(viewingCommission.booking?.pnr)}</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <h4>Influencer Information</h4>
                <div className="detail-row">
                  <label>Name:</label>
                  <span>{getSafeString(viewingCommission.influencer?.name)}</span>
                </div>
                <div className="detail-row">
                  <label>Email:</label>
                  <span>{getSafeString(viewingCommission.influencer?.email)}</span>
                </div>
                <div className="detail-row">
                  <label>Phone:</label>
                  <span>{getSafeString(viewingCommission.influencer?.phone)}</span>
                </div>
                <div className="detail-row">
                  <label>Referral Code:</label>
                  <span>{getSafeString(viewingCommission.couponCode)}</span>
                </div>

                <h4>Booking Information</h4>
                <div className="detail-row">
                  <label>PNR:</label>
                  <span>{getSafeString(viewingCommission.booking?.pnr)}</span>
                </div>
                <div className="detail-row">
                  <label>Route:</label>
                  <span>
                    {getSafeString(viewingCommission.booking?.route?.from)} to {getSafeString(viewingCommission.booking?.route?.to)}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Journey Date:</label>
                  <span>
                    {formatDate(viewingCommission.booking?.journeyDetails?.date)}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Bus Type:</label>
                  <span>{getSafeString(viewingCommission.booking?.journeyDetails?.busType)}</span>
                </div>
                <div className="detail-row">
                  <label>Time:</label>
                  <span>{getSafeString(viewingCommission.booking?.journeyDetails?.time)}</span>
                </div>

                <h4>Pricing Details</h4>
                <div className="detail-row">
                  <label>Booking Amount:</label>
                  <span>₹{getSafeNumber(viewingCommission.bookingAmount)}</span>
                </div>
                <div className="detail-row">
                  <label>Commission Rate:</label>
                  <span>{getSafeNumber(viewingCommission.commissionRate)}%</span>
                </div>
                <div className="detail-row">
                  <label>Commission Amount:</label>
                  <span style={{color: '#2f92c4', fontWeight: '600', fontSize: '16px'}}>
                    ₹{getSafeNumber(viewingCommission.amount)}
                  </span>
                </div>

                <h4>Commission Status</h4>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status-badge ${getSafeString(viewingCommission.status)}`}>
                    {getSafeString(viewingCommission.status).charAt(0).toUpperCase() + getSafeString(viewingCommission.status).slice(1)}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Created Date:</label>
                  <span>{formatDate(viewingCommission.createdAt)}</span>
                </div>
                {viewingCommission.paymentDate && (
                  <div className="detail-row">
                    <label>Payment Date:</label>
                    <span>{formatDate(viewingCommission.paymentDate)}</span>
                  </div>
                )}
                {viewingCommission.updatedAt && (
                  <div className="detail-row">
                    <label>Last Updated:</label>
                    <span>{formatDate(viewingCommission.updatedAt)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {viewingCommission && isFetchingDetails && <SkeletonModal />}


      {processingPayment && (
        <div className="modal-overlay" onClick={closePaymentModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {processingPayment.type === 'single' ? 'Process Influencer Commission' : 'Process Bulk Payment'}
              </h3>
              <button className="modal-close-btn" onClick={closePaymentModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {processingPayment.type === 'single' ? (
                <div className="customer-details">
                  <div className="detail-row">
                    <label>Influencer:</label>
                    <span>{getSafeString(processingPayment.influencer?.name)}</span>
                  </div>
                  <div className="detail-row">
                    <label>Email:</label>
                    <span>{getSafeString(processingPayment.influencer?.email)}</span>
                  </div>
                  <div className="detail-row">
                    <label>Booking PNR:</label>
                    <span>{getSafeString(processingPayment.booking?.pnr)}</span>
                  </div>
                  <div className="detail-row">
                    <label>Amount to Pay:</label>
                    <span style={{color: '#48bb78', fontWeight: '600', fontSize: '18px'}}>
                      ₹{getSafeNumber(processingPayment.amount)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <label>Commission Rate:</label>
                    <span>{getSafeNumber(processingPayment.commissionRate)}%</span>
                  </div>
                </div>
              ) : (
                <div className="customer-details">
                  <div className="detail-row">
                    <label>Total Commissions:</label>
                    <span>{getSafeNumber(processingPayment.commissions?.length)}</span>
                  </div>
                  <div className="detail-row">
                    <label>Total Amount:</label>
                    <span style={{color: '#48bb78', fontWeight: '600', fontSize: '18px'}}>
                      ₹{getSafeNumber(processingPayment.totalAmount)}
                    </span>
                  </div>
                  <div className="influencers-list">
                    <label>Influencers to pay:</label>
                    <ul>
                      {processingPayment.commissions?.map(commission => (
                        <li key={commission._id}>
                          {getSafeString(commission.influencer?.name)} - ₹{getSafeNumber(commission.amount)} ({getSafeString(commission.booking?.pnr)})
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              <div className="payment-date-input">
                <label>Payment Date:</label>
                <input 
                  type="date" 
                  value={bulkPaymentDate}
                  onChange={(e) => setBulkPaymentDate(e.target.value)}
                />
              </div>

              <div style={{marginTop: '20px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px'}}>
                <p style={{margin: '0', color: '#0369a1', fontSize: '14px'}}>
                  <strong>Note:</strong> This will mark the commission(s) as paid and record the payment date. 
                  The influencer(s) will be notified about the payment.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={closePaymentModal}>
                Cancel
              </button>
              <button className="action-btn pay" onClick={handleConfirmPayment}>
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfluencerCommission;